import React, { useState, useEffect } from 'react';
import { pricingIntelligenceAPI } from '../../services/api';

/**
 * HistoricalsTab - Phase 2 content
 * Shows historical contracts table with expandable details and pricing heuristics
 */
const HistoricalsTab = ({
  tender,
  phase2Data,
  loading: externalLoading,
  onStartResponse
}) => {
  const [contracts, setContracts] = useState(phase2Data?.contracts || phase2Data?.historical_contracts || []);
  const [loading, setLoading] = useState(false);
  const [expandedContract, setExpandedContract] = useState(null);
  const [expandedPricing, setExpandedPricing] = useState(0); // auto-expand first row
  const [error, setError] = useState(null);

  // Phase 2 enrichment data (or derive from contracts)
  const derivedAnalysis = React.useMemo(() => {
    // If Phase 2 data exists, use it directly
    if (phase2Data?.bid_recommendation || phase2Data?.arbitrage_heuristic) {
      return {
        bidRecommendation: phase2Data.bid_recommendation,
        arbitrageHeuristic: phase2Data.arbitrage_heuristic,
        pricingComparisons: phase2Data.pricing_comparisons || [],
      };
    }

    // Derive analysis from historical contracts
    if (!contracts || contracts.length === 0) return null;

    const values = contracts
      .map(c => parseFloat(c.award_value || c.value || 0))
      .filter(v => v > 0);

    const dates = contracts
      .map(c => c.award_date || c.date)
      .filter(Boolean)
      .map(d => new Date(d))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a - b);

    const avgValue = values.length > 0
      ? values.reduce((a, b) => a + b, 0) / values.length
      : 0;
    const minValue = values.length > 0 ? Math.min(...values) : 0;
    const maxValue = values.length > 0 ? Math.max(...values) : 0;

    const fmtShort = (v) => {
      if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
      if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
      return `$${v.toFixed(0)}`;
    };

    const dateRange = dates.length >= 2
      ? `${dates[0].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} to ${dates[dates.length - 1].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
      : dates.length === 1
        ? dates[0].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : null;

    // Build heuristic reasoning
    const parts = [];
    if (values.length > 0) {
      parts.push(`Based on ${contracts.length} similar historical contract${contracts.length > 1 ? 's' : ''}, government award values range from ${fmtShort(minValue)} to ${fmtShort(maxValue)} CAD with an average of ${fmtShort(avgValue)} CAD.`);
    }
    if (dateRange) {
      parts.push(`Contract data spans ${dateRange}.`);
    }
    if (values.length >= 3) {
      // Check trend: compare first-half avg vs second-half avg
      const half = Math.floor(values.length / 2);
      const sortedByDate = [...contracts]
        .filter(c => parseFloat(c.award_value || c.value || 0) > 0)
        .sort((a, b) => new Date(a.award_date || a.date || 0) - new Date(b.award_date || b.date || 0));
      const firstHalf = sortedByDate.slice(0, half).map(c => parseFloat(c.award_value || c.value));
      const secondHalf = sortedByDate.slice(half).map(c => parseFloat(c.award_value || c.value));
      const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
      if (avgSecond > avgFirst * 1.1) {
        parts.push('Trend: Award values are increasing over time, suggesting growing budgets in this category.');
      } else if (avgSecond < avgFirst * 0.9) {
        parts.push('Trend: Award values are declining over time, which may indicate increased competition or budget constraints.');
      } else {
        parts.push('Trend: Award values have remained relatively stable across the contract history.');
      }
    }
    if (parts.length === 0) {
      parts.push('Historical contracts found but insufficient pricing data for detailed analysis. Run full AI analysis for deeper insights.');
    }

    // Build per-contract pricing table
    const derivedComparisons = contracts
      .filter(c => parseFloat(c.award_value || c.value || 0) > 0)
      .slice(0, 10)
      .map(c => ({
        product: c.title || c.contract_name || 'Contract',
        historical_avg: parseFloat(c.award_value || c.value || 0),
        award_date: c.award_date || c.date || null,
        supplier: c.supplier || c.buyer_org || null,
      }));

    return {
      bidRecommendation: null,
      arbitrageHeuristic: parts.join(' '),
      pricingComparisons: derivedComparisons,
    };
  }, [phase2Data, contracts]);

  const arbitrageHeuristic = derivedAnalysis?.arbitrageHeuristic;
  const bidRecommendation = derivedAnalysis?.bidRecommendation;
  const pricingComparisons = derivedAnalysis?.pricingComparisons;
  const isDerivedData = !phase2Data?.bid_recommendation && !phase2Data?.arbitrage_heuristic;

  const formatCurrency = (value) => {
    const amount = parseFloat(value || 0);
    if (amount === 0) return 'N/A';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M CAD`;
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  // Load historical contracts if not provided
  useEffect(() => {
    const loadContracts = async () => {
      if (phase2Data?.contracts?.length > 0 || phase2Data?.historical_contracts?.length > 0) {
        setContracts(phase2Data.contracts || phase2Data.historical_contracts);
        return;
      }

      // Get GSIN code from tender
      const gsinCode = tender?.['masterCategory-catégoriePrincipale'] ||
                       tender?.business_intelligence?.master_category ||
                       tender?.procurement_category;

      if (!gsinCode) {
        setContracts([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await pricingIntelligenceAPI.getSimilarContracts(gsinCode, 10);
        setContracts(result.contracts || result.similar_contracts || []);
      } catch (err) {
        console.error('Failed to load historical contracts:', err);
        setError('Unable to load historical contracts');
        setContracts([]);
      } finally {
        setLoading(false);
      }
    };

    loadContracts();
  }, [tender, phase2Data]);

  const getSimilarityClass = (score) => {
    const numScore = parseFloat(score) || 0;
    if (numScore >= 0.8) return 'similarity-high';
    if (numScore >= 0.5) return 'similarity-medium';
    return 'similarity-low';
  };

  const getSimilarityLabel = (score) => {
    const numScore = parseFloat(score) || 0;
    if (numScore >= 0.8) return 'High';
    if (numScore >= 0.5) return 'Medium';
    return 'Low';
  };

  const toggleContract = (contractId) => {
    if (expandedContract === contractId) {
      setExpandedContract(null);
    } else {
      setExpandedContract(contractId);
    }
  };

  if (loading || externalLoading) {
    return (
      <div className="tab-loading">
        <div className="spinner"></div>
        <p>Loading historical contracts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="placeholder-content">
        <div className="placeholder-icon">⚠️</div>
        <div className="placeholder-title">Unable to Load Data</div>
        <div className="placeholder-description">{error}</div>
      </div>
    );
  }

  return (
    <div className="historicals-tab">
      {/* Header with actions */}
      <div className="historicals-header">
        <h3 className="summary-section-title" style={{ marginBottom: 0 }}>
          <span className="icon">📜</span>
          Historical Contracts ({contracts.length})
        </h3>
        <div className="historicals-actions">
          <button className="tab-action-btn" onClick={() => setLoading(true)}>
            Search More Matches
          </button>
          <button className="tab-action-btn">
            Upload Historical Docs
          </button>
        </div>
      </div>

      {/* Contracts Table */}
      {contracts.length > 0 ? (
        <div className="contracts-list">
          <table className="contracts-table">
            <thead>
              <tr>
                <th>Contract Name</th>
                <th>Award Value</th>
                <th>Award Date</th>
                <th>Similarity</th>
                <th>Links</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract, idx) => {
                const contractId = contract.id || contract.contract_id || idx;
                const isExpanded = expandedContract === contractId;
                return (
                  <React.Fragment key={contractId}>
                    <tr onClick={() => toggleContract(contractId)} style={{ cursor: 'pointer' }}>
                      <td>
                        <strong>{contract.title || contract.contract_name || 'Untitled Contract'}</strong>
                        {contract.buyer_org && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {contract.buyer_org}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: 600, color: '#059669' }}>
                        {formatCurrency(contract.award_value || contract.value)}
                      </td>
                      <td>{formatDate(contract.award_date || contract.date)}</td>
                      <td>
                        {contract.similarity_score ? (
                          <span className={`similarity-badge ${getSimilarityClass(contract.similarity_score)}`}>
                            {getSimilarityLabel(contract.similarity_score)}
                          </span>
                        ) : contract.similarity_notes ? (
                          <span className="similarity-badge similarity-medium" title={contract.similarity_notes}>
                            Match
                          </span>
                        ) : (
                          <span className="similarity-badge similarity-low">--</span>
                        )}
                      </td>
                      <td>
                        <div className="contract-links">
                          {(contract.posting_url || contract.link) && (
                            <a
                              href={contract.posting_url || contract.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="contract-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Posting
                            </a>
                          )}
                          {(contract.documents_url || (contract.document_links && contract.document_links.length > 0)) && (
                            <a
                              href={contract.documents_url || contract.document_links[0]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="contract-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Documents
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="tab-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleContract(contractId);
                          }}
                        >
                          {isExpanded ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Detail Panel */}
                    {isExpanded && (
                      <tr>
                        <td colSpan="6" style={{ padding: 0, background: 'transparent' }}>
                          <div className="contract-detail-panel">
                            {/* Summary Grid */}
                            <div className="contract-detail-grid">
                              <div className="contract-detail-item">
                                <div className="label">Total Contract Value</div>
                                <div className="value currency">
                                  {formatCurrency(contract.award_value || contract.value)}
                                </div>
                              </div>
                              {contract.supplier && (
                                <div className="contract-detail-item">
                                  <div className="label">Supplier</div>
                                  <div className="value">{contract.supplier}</div>
                                </div>
                              )}
                              {contract.contract_id && (
                                <div className="contract-detail-item">
                                  <div className="label">Contract ID</div>
                                  <div className="value">{contract.contract_id}</div>
                                </div>
                              )}
                            </div>

                            {/* Similarity Notes */}
                            {contract.similarity_notes && (
                              <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', marginTop: '8px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                <strong style={{ color: '#16a34a' }}>Why similar:</strong> {contract.similarity_notes}
                              </div>
                            )}

                            {/* Document Links */}
                            {contract.document_links && contract.document_links.length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                  Documents ({contract.document_links.length})
                                </h4>
                                {contract.document_links.map((docLink, dIdx) => (
                                  <a
                                    key={dIdx}
                                    href={docLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="contract-link"
                                    style={{ display: 'inline-block', marginRight: '12px', fontSize: '12px' }}
                                  >
                                    Document {dIdx + 1}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="placeholder-content">
          <div className="placeholder-icon">📜</div>
          <div className="placeholder-title">No Historical Contracts Found</div>
          <div className="placeholder-description">
            No similar contracts were found in the historical database.
            Try uploading historical documents or adjusting the search criteria.
          </div>
          <button
            className="tab-action-btn primary"
            style={{ marginTop: '16px' }}
          >
            Upload Historical Documents
          </button>
        </div>
      )}

      {/* Bid Recommendation Badge (Phase 2) */}
      {bidRecommendation && (
        <div className="summary-section" style={{ marginTop: '24px' }}>
          <h3 className="summary-section-title">
            <span className="icon">🎯</span>
            Bid Recommendation
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '14px',
              background: bidRecommendation === 'BID' ? '#dcfce7' :
                         bidRecommendation === 'CONSIDER' ? '#fef3c7' : '#fee2e2',
              color: bidRecommendation === 'BID' ? '#16a34a' :
                     bidRecommendation === 'CONSIDER' ? '#92400e' : '#dc2626'
            }}>
              {bidRecommendation}
            </span>
          </div>
        </div>
      )}

      {/* Arbitrage Analysis — always shown when data available */}
      {arbitrageHeuristic && (
        <div className="summary-section" style={{ marginTop: '16px' }}>
          <h3 className="summary-section-title">
            <span className="icon">📊</span>
            Arbitrage Analysis
            {isDerivedData && (
              <span style={{
                fontSize: '10px',
                fontWeight: 400,
                color: '#9ca3af',
                marginLeft: '8px',
                verticalAlign: 'middle'
              }}>
                (derived from historical data)
              </span>
            )}
          </h3>
          <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
            {typeof arbitrageHeuristic === 'string' ? (
              <p>{arbitrageHeuristic}</p>
            ) : (
              <pre style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                whiteSpace: 'pre-wrap',
                fontSize: '12px'
              }}>
                {JSON.stringify(arbitrageHeuristic, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Pricing Comparisons Table — always shown when data available */}
      {pricingComparisons && pricingComparisons.length > 0 && (
        <div className="summary-section" style={{ marginTop: '16px' }}>
          <h3 className="summary-section-title">
            <span className="icon">💲</span>
            {isDerivedData ? 'Historical Award Values' : 'Arbitrage Analysis'}
          </h3>
          <div className="contracts-list">
            <table className="contracts-table" style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  {isDerivedData ? (
                    <>
                      <th>Contract</th>
                      <th>Award Value</th>
                      <th>Award Date</th>
                      <th>Supplier / Buyer</th>
                    </>
                  ) : (
                    <>
                      <th>Item</th>
                      <th>Our Best Cost</th>
                      <th>Gov't Paid</th>
                      <th>Margin</th>
                      <th>Best Route</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {pricingComparisons.map((item, idx) => {
                  const isExpPricing = expandedPricing === idx;
                  return (
                  <React.Fragment key={idx}>
                    <tr
                      onClick={() => !isDerivedData && setExpandedPricing(isExpPricing ? null : idx)}
                      style={!isDerivedData ? { cursor: 'pointer' } : {}}
                    >
                    {isDerivedData ? (
                      <>
                        <td><strong>{item.product || `Contract ${idx + 1}`}</strong></td>
                        <td style={{ fontWeight: 600, color: '#059669' }}>
                          {item.historical_avg ? formatCurrency(item.historical_avg) : '—'}
                        </td>
                        <td>{item.award_date ? formatDate(item.award_date) : '—'}</td>
                        <td style={{ color: '#6b7280' }}>{item.supplier || '—'}</td>
                      </>
                    ) : (
                      <>
                        <td>
                          <strong>{item.product_name || item.product || item.item_name || `Item ${idx + 1}`}</strong>
                        </td>
                        <td style={{ fontWeight: 600 }}>
                          {item.best_cost_cad ? formatCurrency(item.best_cost_cad) : (item.our_wholesale_estimate ? formatCurrency(item.our_wholesale_estimate) : '—')}
                        </td>
                        <td>{item.historical_price_paid ? formatCurrency(item.historical_price_paid) : '—'}</td>
                        <td style={{ color: '#059669', fontWeight: 600 }}>
                          {item.margin_on_best_cost != null ? `${item.margin_on_best_cost}%` :
                           item.markup_over_wholesale != null ? `${item.markup_over_wholesale}%` : '—'}
                        </td>
                        <td>
                          {(() => {
                            const route = item.sourcing_route || 'unknown';
                            const status = item.validation_status || 'unvalidated';
                            const routeStyles = {
                              domestic: { background: '#dbeafe', color: '#1d4ed8', label: 'Domestic' },
                              factory: { background: '#fce7f3', color: '#be185d', label: 'Factory' },
                              unknown: { background: '#f3f4f6', color: '#6b7280', label: 'AI Est.' },
                            };
                            const badge = routeStyles[route] || routeStyles.unknown;
                            if (status === 'unvalidated' && route === 'unknown') {
                              badge.label = 'AI Est.';
                            }
                            return (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                background: badge.background, color: badge.color,
                              }}>
                                {badge.label}
                                <span style={{ fontSize: '10px', opacity: 0.7 }}>{isExpPricing ? '▲' : '▼'}</span>
                              </span>
                            );
                          })()}
                        </td>
                      </>
                    )}
                    </tr>

                    {/* Expanded proof-of-work detail */}
                    {!isDerivedData && isExpPricing && (
                      <tr>
                        <td colSpan="5" style={{ padding: 0, background: 'transparent' }}>
                          <div style={{
                            padding: '12px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb',
                            borderBottom: '1px solid #e5e7eb', fontSize: '12px', lineHeight: '1.6',
                          }}>
                            {/* Cost comparison grid */}
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' }}>
                              {item.domestic_wholesale_cad && (
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px 12px', minWidth: '140px' }}>
                                  <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Domestic Wholesale</div>
                                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1d4ed8' }}>{formatCurrency(item.domestic_wholesale_cad)}</div>
                                </div>
                              )}
                              {item.factory_landed_cad && (
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px 12px', minWidth: '140px' }}>
                                  <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Factory Landed</div>
                                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#be185d' }}>{formatCurrency(item.factory_landed_cad)}</div>
                                </div>
                              )}
                              {item.historical_price_paid && (
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px 12px', minWidth: '140px' }}>
                                  <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gov't Paid</div>
                                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#059669' }}>{formatCurrency(item.historical_price_paid)}</div>
                                </div>
                              )}
                            </div>

                            {/* Sourcing note */}
                            {item.sourcing_note && (
                              <div style={{ padding: '6px 10px', background: '#eff6ff', borderRadius: '4px', color: '#1e40af', marginBottom: '10px', fontSize: '12px' }}>
                                {item.sourcing_note}
                              </div>
                            )}

                            {/* Price sources — proof of work */}
                            {item.price_sources && item.price_sources.length > 0 && (
                              <div>
                                <div style={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                  Data Sources
                                </div>
                                <ul style={{ margin: 0, paddingLeft: '16px', color: '#374151' }}>
                                  {item.price_sources.map((src, sIdx) => (
                                    <li key={sIdx} style={{ marginBottom: '2px' }}>{src}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!isDerivedData && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
              See the <strong>Financial</strong> tab for full cost breakdown (wholesale, retail, factory landed, web prices).
            </div>
          )}
        </div>
      )}

      {/* Legacy Bid Recommendation Section */}
      {phase2Data?.recommendation && !bidRecommendation && (
        <div className="summary-section" style={{ marginTop: '24px' }}>
          <h3 className="summary-section-title">
            <span className="icon">💡</span>
            Bid Recommendation
          </h3>
          <div className="total-box domestic" style={{ textAlign: 'left' }}>
            <div className="label">Recommended Bid Price</div>
            <div className="value" style={{ fontSize: '24px' }}>
              {formatCurrency(phase2Data.recommendation.recommended_price)}
            </div>
            {phase2Data.recommendation.confidence && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                Confidence: {phase2Data.recommendation.confidence}
              </div>
            )}
            {phase2Data.recommendation.rationale && (
              <div style={{ fontSize: '13px', color: '#374151', marginTop: '12px' }}>
                {phase2Data.recommendation.rationale}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalsTab;
